import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { buildQuoteInquirySchema, QuoteInquirySchema, todayDateString } from './quoteInquirySchema';
import Modal from '../../components/common/Modal';
import DatePicker from '../../components/common/DatePicker';
import { submitQuoteInquiry, RequestType } from './quoteInquiryService';
import RevisionPolicyNotice from '../../components/common/RevisionPolicyNotice';
import { SHIPPING_METHOD_OPTIONS } from '../../types/orderPolicy';
import { DesignType, ORDER_MENU } from '../Order/orderMenu';
import { WowpressApplyPayload } from '../Wowpress/WowpressPricePreview';
import { PortfolioApplyPayload } from '../Portfolio/PortfolioDetail';
import { useUserSession } from '../Auth/userSession';
import {
  BOX_TYPES,
  BUDGET_OPTIONS,
  CONTENT_TYPE_OPTIONS,
  PRINT_OPTIONS,
  REFERENCE_OPTIONS,
  ROUTE_OPTIONS,
} from './boxTypeOptions';
import styles from './QuoteForm.module.scss';

const MAX_FILE_SIZE = 10 * 1024 * 1024;
const ACCEPT_FILE_TYPES = '.jpg,.jpeg,.png,.pdf,.ai';
const TOAST_DURATION_MS = 2800;

function splitPhone(phone: string): { phone1: string; phone2: string; phone3: string } {
  const digits = phone.replace(/[^0-9]/g, '');
  return {
    phone1: digits.slice(0, 3),
    phone2: digits.length > 7 ? digits.slice(3, digits.length - 4) : digits.slice(3, 7),
    phone3: digits.slice(-4),
  };
}

interface QuoteFormProps {
  // inquiry: 견적문의 메뉴 / order: 주문하기 메뉴(디자인 유무 선택 포함)
  mode?: 'inquiry' | 'order';
}

function QuoteForm({ mode = 'inquiry' }: QuoteFormProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [session] = useUserSession();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isOrder = mode === 'order';
  const requestType: RequestType = isOrder ? 'ORDER' : 'INQUIRY';
  const routeState = location.state as {
    wowpressQuote?: WowpressApplyPayload;
    portfolioQuote?: PortfolioApplyPayload;
    designType?: DesignType;
  } | null;
  const wowpressQuote = routeState?.wowpressQuote;
  const portfolioQuote = routeState?.portfolioQuote;

  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [selectedSubs, setSelectedSubs] = useState<Record<string, Set<string>>>({});
  const [files, setFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [toast, setToast] = useState({ message: '', show: false });
  // 납기희망일: 오늘 이전 날짜를 고르면 안내 팝업
  const today = useMemo(() => todayDateString(), []);
  const [isPastDeadlineOpen, setIsPastDeadlineOpen] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<QuoteInquirySchema>({
    resolver: zodResolver(useMemo(() => buildQuoteInquirySchema(!session), [session])),
    defaultValues: {
      title: '',
      company: '',
      name: session?.user.name ?? '',
      password: '',
      email: session?.user.email ?? '',
      ...(session?.user.phone ? splitPhone(session.user.phone) : { phone1: '', phone2: '', phone3: '' }),
      shape: wowpressQuote?.shape ?? portfolioQuote?.shape ?? '',
      quantity: wowpressQuote?.quantity,
      boxSize: '',
      contentSize: '',
      contentType: '',
      deadline: '',
      budget: '',
      print: '',
      route: '',
      content: wowpressQuote?.content ?? portfolioQuote?.content ?? '',
      reference: '',
      shippingMethod: 'SINGLE',
      designType: isOrder ? routeState?.designType ?? 'NONE' : undefined,
      agree: false,
    },
  });

  const agree = watch('agree');
  const selectedRoute = watch('route');
  const selectedShipping = watch('shippingMethod');
  const selectedDesignType = watch('designType');

  const showToast = useCallback((message: string) => {
    setToast({ message, show: true });
    window.setTimeout(() => setToast((t) => ({ ...t, show: false })), TOAST_DURATION_MS);
  }, []);

  useEffect(() => {
    if (wowpressQuote) {
      showToast('선택하신 인쇄 상품 정보를 반영했습니다. 아래 내용을 확인해주세요.');
    } else if (portfolioQuote) {
      showToast('선택하신 상품 정보를 반영했습니다. 아래 내용을 확인해주세요.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePickerClick = (cat: string) => {
    if (activeCat === cat) {
      setActiveCat(null);
      setValue('shape', '');
      return;
    }
    setActiveCat(cat);
    const box = BOX_TYPES.find((b) => b.cat === cat);
    if (box && !getValues('shape').trim()) {
      setValue('shape', `${box.name} `, { shouldValidate: true });
    }
  };

  const toggleSub = (cat: string, value: string) => {
    setSelectedSubs((prev) => {
      const next = new Set(prev[cat] ?? []);
      const willSelect = !next.has(value);
      if (willSelect) {
        next.add(value);
        const current = getValues('shape').trim();
        if (!current.includes(value)) {
          setValue('shape', current ? `${current} / ${value}` : value, { shouldValidate: true });
        }
      } else {
        next.delete(value);
      }
      return { ...prev, [cat]: next };
    });
  };

  const addFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const accepted: File[] = [];
    Array.from(list).forEach((f) => {
      if (f.size > MAX_FILE_SIZE) {
        showToast('10MB 초과 파일은 E-mail로 부탁드립니다.');
        return;
      }
      accepted.push(f);
    });
    if (accepted.length > 0) setFiles((prev) => [...prev, ...accepted]);
  };

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const onSubmit = async (values: QuoteInquirySchema) => {
    try {
      const result = await submitQuoteInquiry(values, files, session?.token, requestType);
      navigate('/quote/complete', { state: { orderNo: result.orderNo, requestType } });
    } catch (err) {
      showToast(err instanceof Error ? err.message : '접수 중 오류가 발생했습니다.');
    }
  };

  const onInvalid = () => {
    showToast('필수 항목을 입력해주세요.');
  };

  const isSubmitDisabled = !agree || isSubmitting;

  return (
    <>
      <form id="quoteForm" className={styles.form} onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate>
        {/* 01. 고객정보 */}
        <section className={styles.sectionBlock}>
          <header className={styles.sectionHead}>
            <h2 className={styles.sectionLabel}>
              고객정보 <span className={styles.req}>*</span>
            </h2>
            <p className={styles.sectionHint}>필수 입력 사항입니다. 정확한 견적 안내를 위해 정확히 입력해주세요.</p>
          </header>
          <div className={styles.fieldGrid}>
            <div className={`${styles.field} ${styles.full}`}>
              <label>
                제목 <span className={styles.req}>*</span>
              </label>
              <input
                type="text"
                className={`${styles.textInput} ${errors.title ? styles.err : ''}`}
                placeholder="예) 화장품 시딩박스 500개 견적 문의드립니다"
                {...register('title')}
              />
            </div>
            <div className={styles.field}>
              <label>
                업체명 <span className={styles.req}>*</span>
              </label>
              <input
                type="text"
                className={`${styles.textInput} ${errors.company ? styles.err : ''}`}
                placeholder="회사 / 브랜드명"
                {...register('company')}
              />
            </div>
            <div className={styles.field}>
              <label>
                이름 <span className={styles.req}>*</span>
              </label>
              <input
                type="text"
                className={`${styles.textInput} ${errors.name ? styles.err : ''}`}
                placeholder="담당자 이름"
                {...register('name')}
              />
            </div>
            {!session && (
              <div className={styles.field}>
                <label>
                  주문 비밀번호 <span className={styles.req}>*</span>
                </label>
                <input
                  type="password"
                  className={`${styles.textInput} ${errors.password ? styles.err : ''}`}
                  placeholder="'주문조회'에서 확인할 때 사용할 비밀번호"
                  {...register('password')}
                />
              </div>
            )}
            <div className={styles.field}>
              <label>
                이메일 <span className={styles.req}>*</span>
              </label>
              <input
                type="email"
                className={`${styles.textInput} ${errors.email ? styles.err : ''}`}
                placeholder="hello@brand.com"
                {...register('email')}
              />
            </div>
            <div className={`${styles.field} ${styles.full}`}>
              <label>
                연락처 <span className={styles.req}>*</span>
              </label>
              <div className={styles.phoneRow}>
                <input
                  type="tel"
                  className={`${styles.textInput} ${errors.phone1 ? styles.err : ''}`}
                  placeholder="010"
                  maxLength={3}
                  {...register('phone1')}
                />
                <input
                  type="tel"
                  className={`${styles.textInput} ${errors.phone2 ? styles.err : ''}`}
                  placeholder="0000"
                  maxLength={4}
                  {...register('phone2')}
                />
                <input
                  type="tel"
                  className={`${styles.textInput} ${errors.phone3 ? styles.err : ''}`}
                  placeholder="0000"
                  maxLength={4}
                  {...register('phone3')}
                />
              </div>
            </div>
          </div>
        </section>

        {/* 주문 유형: 주문하기 메뉴에서만 — 디자인 유무 */}
        {isOrder && (
          <section className={styles.sectionBlock}>
            <header className={styles.sectionHead}>
              <h2 className={styles.sectionLabel}>
                디자인 유무 <span className={styles.req}>*</span>
              </h2>
              <p className={styles.sectionHint}>
                {ORDER_MENU.find((m) => m.designType === selectedDesignType)?.desc ??
                  '디자인 파일 보유 여부를 선택해주세요.'}
              </p>
            </header>
            <div className={styles.radioPills}>
              {ORDER_MENU.map((m) => (
                <label
                  key={m.designType}
                  className={`${styles.radioPill} ${selectedDesignType === m.designType ? styles.checked : ''}`}
                >
                  <input type="radio" value={m.designType} {...register('designType')} />
                  <span className={styles.dot}></span>
                  {m.label}
                </label>
              ))}
            </div>
          </section>
        )}

        {/* 02. 박스형태 참고이미지 */}
        <section className={styles.sectionBlock}>
          <header className={styles.sectionHead}>
            <h2 className={styles.sectionLabel}>박스형태 참고이미지</h2>
            <p className={styles.sectionHint}>원하시는 박스 형태를 선택하시면 세부 옵션이 나타납니다. (선택 사항)</p>
          </header>

          <div className={styles.pickerMapper}>
            {BOX_TYPES.map((box) => (
              <div
                key={box.cat}
                className={`${styles.picker} ${activeCat === box.cat ? styles.active : ''}`}
                onClick={() => handlePickerClick(box.cat)}
              >
                <div className={styles.pickerSvg}>{box.icon}</div>
                <div className={styles.pickerName}>{box.name}</div>
              </div>
            ))}
          </div>

          {BOX_TYPES.map((box) => (
            <div key={box.cat} className={`${styles.subPanel} ${activeCat === box.cat ? styles.show : ''}`}>
              <div className={styles.subPanelTitle}>
                {box.name} — 세부 옵션
              </div>
              <div className={styles.subPills}>
                {box.subs.map((sub) => {
                  const isSelected = selectedSubs[box.cat]?.has(sub.value) ?? false;
                  return (
                    <label
                      key={sub.value}
                      className={`${styles.subPill} ${isSelected ? styles.selected : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSub(box.cat, sub.value)}
                      />
                      <span className={styles.check}></span>
                      {sub.label}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </section>

        {/* 03. 제작 사양 */}
        <section className={styles.sectionBlock}>
          <header className={styles.sectionHead}>
            <h2 className={styles.sectionLabel}>
              제작 사양 <span className={styles.req}>*</span>
            </h2>
            <p className={styles.sectionHint}>필수 입력 항목입니다. 정확한 견적 산출을 위해 가능한 자세히 입력해주세요.</p>
          </header>
          <div className={styles.fieldGrid}>
            <div className={styles.field}>
              <label>
                패키지 형태 <span className={styles.req}>*</span>
                <span className={styles.optMark}>※ 상단 참고</span>
              </label>
              <input
                type="text"
                className={`${styles.textInput} ${errors.shape ? styles.err : ''}`}
                placeholder="예) 싸바리박스 2단형"
                {...register('shape')}
              />
            </div>
            <div className={styles.field}>
              <label>
                제작수량 <span className={styles.req}>*</span>
              </label>
              <div className={styles.withUnit}>
                <input
                  type="number"
                  min={1}
                  className={`${styles.textInput} ${errors.quantity ? styles.err : ''}`}
                  placeholder="500"
                  {...register('quantity', { valueAsNumber: true })}
                />
                <span className={styles.unit}>개</span>
              </div>
            </div>
            <div className={styles.field}>
              <label>
                박스 사이즈 (W × H × D) <span className={styles.req}>*</span>
              </label>
              <div className={styles.withUnit}>
                <input
                  type="text"
                  className={`${styles.textInput} ${errors.boxSize ? styles.err : ''}`}
                  placeholder="120 × 80 × 35"
                  {...register('boxSize')}
                />
                <span className={styles.unit}>mm</span>
              </div>
            </div>
            <div className={styles.field}>
              <label>내용물 사이즈</label>
              <div className={styles.withUnit}>
                <input
                  type="text"
                  className={styles.textInput}
                  placeholder="110 × 70 × 30"
                  {...register('contentSize')}
                />
                <span className={styles.unit}>mm</span>
              </div>
            </div>
            <div className={styles.field}>
              <label>내용물 종류</label>
              <select className={styles.selectInput} {...register('contentType')}>
                <option value="">선택</option>
                {CONTENT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.field}>
              <label>납기희망일</label>
              {/* 입력칸 어디를 눌러도 열리는 달력 (지난 날짜를 누르면 안내 팝업) */}
              <input type="hidden" {...register('deadline')} />
              <DatePicker
                value={watch('deadline') ?? ''}
                min={today}
                onChange={(v) => setValue('deadline', v, { shouldValidate: !!errors.deadline })}
                onPastSelect={() => setIsPastDeadlineOpen(true)}
                placeholder="납기희망일 선택"
                inputClassName={styles.textInput}
                hasError={!!errors.deadline}
              />
            </div>
            <div className={styles.field}>
              <label>예산 범위</label>
              <select className={styles.selectInput} {...register('budget')}>
                <option value="">선택</option>
                {BUDGET_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
            <div className={`${styles.field} ${styles.full}`}>
              <label>인쇄 옵션</label>
              <select className={styles.selectInput} {...register('print')}>
                {PRINT_OPTIONS.map((opt) => (
                  <option key={opt.label} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={`${styles.field} ${styles.full}`}>
              <label>유입경로</label>
              <div className={styles.radioPills}>
                {ROUTE_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={`${styles.radioPill} ${selectedRoute === opt.value ? styles.checked : ''}`}
                  >
                    <input type="radio" value={opt.value} {...register('route')} />
                    <span className={styles.dot}></span>
                    {opt.label}
                  </label>
                ))}
              </div>
            </div>
            <div className={`${styles.field} ${styles.full}`}>
              <label>
                문의내용 <span className={styles.req}>*</span>
              </label>
              <textarea
                className={`${styles.textArea} ${errors.content ? styles.err : ''}`}
                placeholder="원하시는 박스의 형태, 인쇄, 마감, 시안 참고자료, 유통 시기 등 자세히 작성해주시면 더 정확한 견적 안내가 가능합니다."
                {...register('content')}
              />
            </div>
          </div>
        </section>

        {/* 배송 방법 */}
        <section className={styles.sectionBlock}>
          <header className={styles.sectionHead}>
            <h2 className={styles.sectionLabel}>
              배송 방법 <span className={styles.req}>*</span>
            </h2>
            <p className={styles.sectionHint}>기본은 단건발송입니다. 여러 주문을 한 번에 받으시려면 일괄배송을 선택해주세요.</p>
          </header>
          <div className={styles.radioPills}>
            {SHIPPING_METHOD_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`${styles.radioPill} ${selectedShipping === opt.value ? styles.checked : ''}`}
              >
                <input type="radio" value={opt.value} {...register('shippingMethod')} />
                <span className={styles.dot}></span>
                {opt.label}
              </label>
            ))}
          </div>
          <ul className={styles.shippingNotice}>
            {SHIPPING_METHOD_OPTIONS.map((opt) => (
              <li key={opt.value} className={selectedShipping === opt.value ? styles.current : ''}>
                <strong>{opt.label}</strong> {opt.desc}
              </li>
            ))}
            <li>배송비는 상품 부피 · 무게와 배송지에 따라 달라지며, 담당자가 결제금액 확정 시 함께 안내해드립니다.</li>
          </ul>
        </section>

        {/* 04. 첨부파일 */}
        <section className={styles.sectionBlock}>
          <header className={styles.sectionHead}>
            <h2 className={styles.sectionLabel}>
              {isOrder && selectedDesignType === 'HAVE' ? '디자인(인쇄) 파일' : '첨부파일'}
            </h2>
            <p className={styles.sectionHint}>AI / PDF / JPG / PNG (최대 10MB) · 10MB 초과 시 하단 E-mail로 부탁드립니다.</p>
          </header>
          <div
            className={`${styles.dropzone} ${isDragOver ? styles.dragOver : ''}`}
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragOver(true);
            }}
            onDragLeave={() => setIsDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragOver(false);
              addFiles(e.dataTransfer.files);
            }}
          >
            <div className={styles.dropzoneIcon}>
              <svg viewBox="0 0 24 24">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <path d="m7 10 5-5 5 5" />
                <path d="M12 5v12" />
              </svg>
            </div>
            <div className={styles.dropzoneText}>
              파일을 끌어다 놓거나 <strong style={{ color: 'var(--accent-deep)' }}>클릭</strong>하여 업로드
            </div>
            <div className={styles.dropzoneHint}>JPG · PNG · PDF · AI · 최대 10MB</div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPT_FILE_TYPES}
              style={{ display: 'none' }}
              onChange={(e) => {
                addFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </div>
          <div className={styles.fileList}>
            {files.map((f, i) => (
              <div key={`${f.name}-${i}`} className={styles.fileRow}>
                <svg viewBox="0 0 24 24">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <path d="M14 2v6h6" />
                </svg>
                <div className={styles.fileName}>{f.name}</div>
                <div className={styles.fileSize}>{(f.size / 1024).toFixed(1)} KB</div>
                <button type="button" className={styles.fileRemove} aria-label="삭제" onClick={() => removeFile(i)}>
                  <svg viewBox="0 0 24 24">
                    <path d="m6 6 12 12M6 18 18 6" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </section>

        {/* 05. 참고 포트폴리오 */}
        <section className={styles.sectionBlock}>
          <header className={styles.sectionHead}>
            <h2 className={styles.sectionLabel}>참고 포트폴리오</h2>
            <p className={styles.sectionHint}>
              원하시는 스타일에 가장 가까운 지난 작업물을 선택하면 담당자가 해당 시안을 참고합니다.
            </p>
          </header>
          <div className={styles.field}>
            <select className={styles.selectInput} {...register('reference')}>
              <option value="">선택</option>
              {REFERENCE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </section>

        {/* 시안 수정 정책 안내 (주문 접수 단계) */}
        <section className={styles.sectionBlock}>
          <RevisionPolicyNotice />
        </section>

        {/* 06. 개인정보 동의 */}
        <section className={styles.sectionBlock}>
          <header className={styles.sectionHead}>
            <h2 className={styles.sectionLabel}>
              개인정보 수집 및 이용 동의 <span className={styles.req}>*</span>
            </h2>
          </header>
          <div className={styles.privacyCard}>
            <div className={styles.privacyHead}>
              <div className={styles.privacyTitle}>개인정보처리방침 요약</div>
              <div
                className={`${styles.toggle} ${agree ? styles.on : ''}`}
                role="switch"
                aria-checked={agree}
                tabIndex={0}
                onClick={() => setValue('agree', !agree, { shouldValidate: true })}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault();
                    setValue('agree', !agree, { shouldValidate: true });
                  }
                }}
              ></div>
            </div>
            <div className={styles.privacyBody}>
              <p style={{ fontSize: '13px', color: 'var(--ink)', marginBottom: '10px' }}>
                (주)대흥사는 기업 / 단체 및 개인의 정보 수집 및 이용 등 처리에 있어 아래의 사항을 관계법령에 따라 고지하고
                안내해 드립니다.
              </p>
              <ol>
                <li>
                  <strong style={{ color: 'var(--ink)' }}>정보수집의 이용 목적</strong> — 상담 및 제작 의뢰 진행
                </li>
                <li>
                  <strong style={{ color: 'var(--ink)' }}>수집 · 이용 항목</strong> — 이름, 연락처, 이메일, 문의 내용 등
                </li>
                <li>
                  <strong style={{ color: 'var(--ink)' }}>보유 및 이용기간</strong> — 상담 종료 후 6개월 / 정보 제공자의
                  삭제 요청 시 즉시
                </li>
                <li>
                  <strong style={{ color: 'var(--ink)' }}>개인정보처리책임자</strong> — 전화 031-925-6547 / E-mail
                  hello@havenpack.kr
                </li>
              </ol>
            </div>
            <div className={styles.privacyFoot}>
              <label className={styles.privacyConsent}>
                <input type="checkbox" {...register('agree')} />
                개인정보 수집 및 이용에 동의합니다.
              </label>
              <button type="button" className={styles.btnView}>
                전문보기
                <svg viewBox="0 0 24 24">
                  <path d="M7 17 17 7" />
                  <path d="M7 7h10v10" />
                </svg>
              </button>
            </div>
          </div>
        </section>
      </form>

      {/* Sticky Action Bar */}
      <div className={styles.actionBar}>
        <div className={styles.actionInfo}>
          <span>
            <span className={styles.check}>✓</span> {isOrder ? '주문서' : '견적문의'} 작성 후{' '}
            <strong>담당자 회신까지 평균 24시간</strong> 이내
          </span>
        </div>
        <div className={styles.actionButtons}>
          <button type="button" className={styles.btnSecondaryAction} onClick={() => navigate('/')}>
            취소
          </button>
          <button type="submit" form="quoteForm" className={styles.btnPrimaryAction} disabled={isSubmitDisabled}>
            {isSubmitting ? '접수 중...' : isOrder ? '주문서 제출' : '견적문의 제출'}
            <svg viewBox="0 0 24 24">
              <path d="M5 12h14" />
              <path d="m13 6 6 6-6 6" />
            </svg>
          </button>
        </div>
      </div>

      {/* Toast */}
      <div className={`${styles.toast} ${toast.show ? styles.show : ''}`}>{toast.message}</div>

      {/* 납기희망일 지난 날짜 안내 */}
      <Modal isOpen={isPastDeadlineOpen} onClose={() => setIsPastDeadlineOpen(false)}>
        <div className={styles.alertBox} role="alertdialog" aria-labelledby="pastDeadlineTitle">
          <h2 id="pastDeadlineTitle">선택할 수 없는 날짜입니다</h2>
          <p>납기희망일은 오늘 이전 날짜를 선택할 수 없습니다. 오늘 이후 날짜를 선택해주세요.</p>
          <button type="button" className={styles.alertButton} onClick={() => setIsPastDeadlineOpen(false)}>
            확인
          </button>
        </div>
      </Modal>
    </>
  );
}

export default QuoteForm;
